import { getTagPrintingTemplatesForOffline } from '@/utils/tag-printing';
import {
  PrintTagTemplateDetailFieldsFragment,
  TagPrint_BatchTemplateQuery,
  TagPrint_BatchTemplateQueryVariables,
} from '@/types';
import { TAG_PRINT_BATCH_TEMPLATE_QUERY } from '@/components/tag-print-batch-queries';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useMemo } from 'react';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useOfflineTagBatchPrint } from '@/hooks/use-offline-data';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

interface Options {
  /**
   * Restricts the result to templates the app can render on device. Rendering
   * locally needs a text template, so a template without one cannot be used
   * where the values come from the device rather than the server.
   */
  requireLocalRendering?: boolean;
}

/**
 * The print tag templates assigned to the current restaurant, read from the
 * offline store or the API depending on connectivity.
 *
 * Callers share this rather than resolving templates themselves so that the
 * rule for which templates are usable, and where they are read from, live in
 * one place.
 */
export const usePrintTagTemplates = ({
  requireLocalRendering = false,
}: Options = {}) => {
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const isOfflineModeActive = useOfflineModeActive();

  const {
    data,
    error,
    loading: templatesLoading,
    refetch,
  } = useWrappedQuery<
    TagPrint_BatchTemplateQuery,
    TagPrint_BatchTemplateQueryVariables
  >(TAG_PRINT_BATCH_TEMPLATE_QUERY, {
    fetchPolicy: 'network-only',
    skip: isOfflineModeActive,
    variables: {
      restaurantUUID,
      supplierUUID,
    },
  });

  const {
    loading: offlineTemplatesLoading,
    tagBatchPrintData: offlineTagBatchPrintData,
  } = useOfflineTagBatchPrint(restaurantUUID, supplierUUID);

  const offlineTemplates = offlineTagBatchPrintData?.templates;

  const templates: PrintTagTemplateDetailFieldsFragment[] = useMemo(() => {
    const assignedTemplates = isOfflineModeActive
      ? Object.values(offlineTemplates ?? {})
      : (data?.print_tag_template_assignments.map(
          (assignment) => assignment.print_tag_template,
        ) ?? []);

    // Offline every tag is rendered on device, so the same restriction applies
    // there whether or not the caller asked for it.
    return isOfflineModeActive || requireLocalRendering
      ? getTagPrintingTemplatesForOffline(assignedTemplates)
      : assignedTemplates;
  }, [
    data?.print_tag_template_assignments,
    isOfflineModeActive,
    offlineTemplates,
    requireLocalRendering,
  ]);

  return {
    error,
    // The query is skipped offline, so its loading flag stays false while the
    // stored templates are still being read.
    loading: isOfflineModeActive ? offlineTemplatesLoading : templatesLoading,
    refetch,
    templates,
  };
};
